/// <amd-module name="Build/Queue-Panel/DemandsControllerView" />
import * as React from "react";

import { DemandPanel } from "Build/Queue-Panel/DemandPanel";
import * as Resources from "Build/Queue-Panel/Resources";
import { QueueBuildStore } from "Build/Queue-Panel/Stores/QueueBuild";
import { DemandCondition, IDemandData } from "Types";

import { Button } from "VSSUI/Button";
import { Icon, IconSize } from "VSSUI/Icon";
import { IListItemDetails, IListRow, ListSelection, ListItem, ScrollableList } from "VSSUI/List";
import { Link } from "VSSUI/Link";
import { Panel } from "VSSUI/Panel";
import { getSafeId } from "VSSUI/Util";

import { IObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";

const c_demandsHelpLink = "https://go.microsoft.com/fwlink/?linkid=2087182";

export interface IDemandsControllerViewProps {
    onDismiss: () => void;
    pageContext: IVssPageContext;
    pipelineName: string;
    store: QueueBuildStore;
}

export class DemandsControllerView extends React.Component<IDemandsControllerViewProps> {
    private _listSelectionIndex: number;
    private _defaultListSelectionIndex: number = -1;
    private _itemProvider: IObservableArray<IDemandData>;

    constructor(props: IDemandsControllerViewProps)  {
        super(props);
        this._itemProvider = this.props.store.demands;
        this._listSelectionIndex = this._defaultListSelectionIndex;
    }

    public render(): JSX.Element {
        return (
            <Panel
                backButtonProps={{ onClick: this.props.onDismiss }}
                contentClassName="queue-panel-child no-padding"
                description={this.props.pipelineName}
                escDismiss={true}
                footerButtonProps={[{
                    text: Resources.AddDemandButton,
                    primary: true,
                    onClick: this._createDemand
                }]}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: Resources.DemandsTitle }}
            >
                <div className="queue-panel-list-container">
                    <div className="flex-row scroll-auto" onKeyDown={this._onKeyDown}>
                        <ScrollableList
                            ariaLabel={Resources.DemandsTitle}
                            itemProvider={this._itemProvider}
                            renderRow={this._renderRow}
                            width="100%"
                            singleClickActivation={true}
                            onActivate={this._updateDemand}
                            outerClassName="custom-scrollbar"
                            role="list"
                        />
                    </div>
                    <Observer demands={this._itemProvider}>
                        {(props: { demands: IDemandData[] }) =>
                            props.demands.length === 0 ? (
                                <span className="flex-row margin-vertical-8 padding-horizontal-20 primary-text">
                                    <label className="margin-right-4">{Resources.EmptyDemandsDescription}</label>
                                    <Link href={c_demandsHelpLink} ariaLabel={Resources.DemandsLearnMore}>
                                        {Resources.LearnMoreMessage}
                                    </Link>
                                </span>
                            ) : null
                        }
                    </Observer>
                </div>
            </Panel>
        );
    }

    private _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // If delete key is pressed with a demand selected, delete it
        const key = event.key.toLowerCase();
        if (key === "delete" && this._listSelectionIndex != this._defaultListSelectionIndex) {
            this.props.store.deleteDemand(this._listSelectionIndex);
            event.preventDefault();
        }
    }

    private _onFocusItem(rowIndex: number) {
        this._listSelectionIndex = rowIndex;
    }

    private _renderRow = (index: number,  item: IDemandData, details: IListItemDetails<IDemandData>): JSX.Element => {
        const message = item.name ? format(Resources.DeleteButtonTooltip, item.name) : Resources.DeleteButtonEmptyTooltip;
        const value = item.condition === DemandCondition.Equals ? "= " + item.value : Resources.ExistsCondition;
        const hiddenAriaDivSafeId = getSafeId(this.c_hiddenAriaDivId);
        details.onFocusItem = rowIndex => this._onFocusItem(rowIndex);

        return (
            <ListItem key={"list-item" + index} index={index} details={details}>
                <div className="queue-panel-list-row flex-grow flex-row">
                    <div className="flex-column justify-center" aria-describedby={hiddenAriaDivSafeId}>
                        <span className="text-ellipsis primary-text" aria-label={item.name}>{item.name}</span>
                        <span className="font-size text-ellipsis secondary-text" aria-label={value}>{value}</span>
                        <div id={hiddenAriaDivSafeId} className="hidden">{Resources.ListItemRoleDescription}</div>
                    </div>
                    <span className="flex-grow flex-row justify-end">
                        <div className="flex-column justify-center margin-right-16">
                            <Button
                                ariaLabel={message} 
                                className="bolt-list-cell-content-reveal"
                                iconProps={{ iconName: "Delete" }}
                                onClick={event => {
                                    this.props.store.deleteDemand(index);
                                    event.preventDefault();
                                }}
                                subtle={true}
                                tooltipProps={{ text: message }}
                            />
                        </div>
                        <Icon iconName="ChevronRightMed" size={IconSize.medium} />
                    </span>
                </div>
            </ListItem>
        );
    }

    private _createDemand = () => {
        const newDemand = {
            index: this.props.store.demands.length,
            condition: DemandCondition.Exists
        } as IDemandData;

        this._renderDemandPanel(newDemand, true);
    }

    private _updateDemand = (event: React.SyntheticEvent<HTMLElement, Event>, listRow: IListRow<IDemandData>) => {
        this._renderDemandPanel({ ...listRow.data }, false);
    }

    private _renderDemandPanel = (demand: IDemandData, isNewDemand: boolean) => {
        const layoutManager = this.props.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            return React.createElement(DemandPanel, {
                demand: demand,
                isNewDemand: isNewDemand,
                onDismiss: onDismiss,
                onDemandAdded: (demand) => this.props.store.addDemand(demand),
                onDemandUpdated: (demand) => this.props.store.updateDemand(demand)
            });
        });
    }

    private c_hiddenAriaDivId = "hiddenAriaDiv";
}
